/**
 * Blog content, sourced from code.
 *
 * There's no CMS/backend behind this yet, so each post is just an object
 * here — title, excerpt, body paragraphs, and a cover photo.
 * To publish a new post: add an object to this array, and put its cover in
 * `src/assets/blog/` as two WebP files (480px and 960px wide, 16:9) named
 * `<slug>-480.webp` / `<slug>-960.webp`, then import them below.
 *
 * Current covers are free photos from Unsplash (Unsplash License: free for
 * commercial use, no attribution required); photo IDs are noted per post.
 *
 * When this needs to scale beyond a handful of hand-written posts (e.g.
 * non-developers publishing on their own, or the blog needs to be updated
 * without a code deploy), the natural next step — see README — is a
 * git-based CMS like Decap CMS, since this repo already deploys through
 * Netlify: no server to run, posts are just markdown files an editor UI
 * writes into the repo, and a rebuild picks them up automatically.
 */

import techStackSmall from '../assets/blog/choosing-the-right-tech-stack-480.webp';
import techStackLarge from '../assets/blog/choosing-the-right-tech-stack-960.webp';
import aiAdoptionSmall from '../assets/blog/ai-adoption-without-the-hype-480.webp';
import aiAdoptionLarge from '../assets/blog/ai-adoption-without-the-hype-960.webp';
import onTimeDeliverySmall from '../assets/blog/why-on-time-delivery-is-a-process-problem-480.webp';
import onTimeDeliveryLarge from '../assets/blog/why-on-time-delivery-is-a-process-problem-960.webp';
import legacyMigrationSmall from '../assets/blog/legacy-system-migrations-that-dont-break-things-480.webp';
import legacyMigrationLarge from '../assets/blog/legacy-system-migrations-that-dont-break-things-960.webp';
import clientPartnershipSmall from '../assets/blog/what-makes-a-good-client-partnership-480.webp';
import clientPartnershipLarge from '../assets/blog/what-makes-a-good-client-partnership-960.webp';

export const blogPosts = [
  {
    slug: 'choosing-the-right-tech-stack',
    title: 'Choosing the Right Tech Stack for Your Next Project',
    excerpt: 'The framework you pick on day one shapes your hiring, your budget, and your ceiling for the next five years. Here’s how we think about it.',
    date: '2026-08-18',
    category: 'Engineering',
    // unsplash.com/photos/gCyjEr-g2oI
    cover: { small: techStackSmall, large: techStackLarge, alt: "A developer workstation with code on multiple monitors" },
    content: [
      'Every project kickoff eventually hits the same question: what should we actually build this with? It’s tempting to default to whatever the team already knows, and often that’s the right call — but not always.',
      'We weigh three things before recommending a stack: the team that will maintain it after launch, the scaling shape of the product (steady growth vs. unpredictable spikes), and the hiring pool you’ll draw from later. A brilliant but obscure stack can quietly become a liability once the original developers move on.',
      'For most business web applications, a boring, well-supported stack beats a novel one. Boring means more Stack Overflow answers, more available engineers, and fewer surprises in production.',
    ],
  },
  {
    slug: 'ai-adoption-without-the-hype',
    title: 'AI Adoption Without the Hype: A Practical Starting Point',
    excerpt: 'Most businesses don’t need a custom LLM. They need one well-scoped automation that saves someone two hours a day.',
    date: '2026-07-30',
    category: 'AI & Automation',
    // unsplash.com/photos/oLthDWAG244
    cover: { small: aiAdoptionSmall, large: aiAdoptionLarge, alt: "A laptop showing ChatGPT, an AI assistant" },
    content: [
      'When clients come to us wanting "AI," the useful first question is: AI doing what, for whom, saving how much time? Vague ambition rarely survives contact with a real budget.',
      'The engagements that go well tend to start narrow — automating a specific, repetitive decision (routing a support ticket, flagging an anomalous invoice, drafting a first-pass reply) rather than trying to reinvent an entire workflow at once.',
      'Once that narrow win is in production and trusted, it’s much easier to justify expanding scope — and you’ve already built the monitoring and fallback logic you’ll need for the bigger version.',
    ],
  },
  {
    slug: 'why-on-time-delivery-is-a-process-problem',
    title: 'Why On-Time Delivery Is a Process Problem, Not a Talent Problem',
    excerpt: 'Late projects are rarely caused by slow developers. They’re caused by unclear scope discovered too late.',
    date: '2026-07-12',
    category: 'Delivery',
    // unsplash.com/photos/JXyY3jRV9oI
    cover: { small: onTimeDeliverySmall, large: onTimeDeliveryLarge, alt: "A project planning board with tasks organised into columns" },
    content: [
      'It’s a comforting story to blame a slipped deadline on the developers being slow. In our experience, the real cause is almost always upstream: a requirement that wasn’t actually agreed on, discovered three weeks into a sprint.',
      'The fix isn’t working faster — it’s finding out what you don’t know earlier. That means investing real time in discovery before a single line of code is written, and treating changing requirements mid-sprint as a signal to revisit, not just absorb.',
    ],
  },
  {
    slug: 'legacy-system-migrations-that-dont-break-things',
    title: 'Legacy System Migrations That Don’t Break Things',
    excerpt: 'Zero downtime isn’t luck. It’s a specific, unglamorous sequence of steps most teams skip under deadline pressure.',
    date: '2026-06-20',
    category: 'Engineering',
    // unsplash.com/photos/2JJ3wBHu4_0
    cover: { small: legacyMigrationSmall, large: legacyMigrationLarge, alt: "A server rack in a data centre" },
    content: [
      'Migrating off a legacy system is one of the highest-risk, lowest-glory projects in software. Nobody notices when it goes well; everybody notices when it doesn’t.',
      'The pattern that’s worked for us: run the new system alongside the old one, mirror writes to both, and only cut over reads once the two have agreed for long enough that you trust it. It’s slower than a hard cutover — and it’s the reason we can promise zero data loss.',
    ],
  },
  {
    slug: 'what-makes-a-good-client-partnership',
    title: 'What Makes a Good Client Partnership, From the Vendor Side',
    excerpt: 'The best projects we’ve delivered all had one thing in common, and it wasn’t budget.',
    date: '2026-05-29',
    category: 'Culture',
    // unsplash.com/photos/6-iunIrJtbQ
    cover: { small: clientPartnershipSmall, large: clientPartnershipLarge, alt: "Two business partners shaking hands" },
    content: [
      'Budget matters, obviously. But looking back at our best-run engagements, the common thread was a single point of contact on the client side who could actually make decisions — and made them quickly.',
      'Projects stall not because the work is hard, but because a decision is waiting on someone who isn’t in the room. If you’re evaluating a technology partner, ask them how they handle that — not just how many developers they’ll staff.',
    ],
  },
];

export function getPostBySlug(slug) {
  return blogPosts.find(p => p.slug === slug);
}
